import { ChangeSet } from "@codemirror/state";
import { Data, Effect, Match, Schema } from "effect";
import { classifyAnnotationTerminal } from "./annotation-terminal.ts";
import { type AuditEvent, Author } from "./audit.ts";
import type {
  CollabVersion,
  DiagnosticAnchor,
  DiagnosticsPayload,
  UpdateLog,
  UpdateLogEntry,
} from "./collab.ts";
import type { FindingSeverity } from "./finding-summary.ts";
import {
  AnnotationId,
  type DocumentId,
  EditProposalId,
  RuleContentHash,
  SuggestionId,
} from "./ids.ts";
import { isManualAnnotation, isManualSentinel } from "./manual.ts";
import { AnnotationScope, EditOperation, Withdraw } from "./review.ts";
import { canonicalizeRuleId } from "./rule-id.ts";
import { deriveDisplayName } from "./viewer.ts";

// `ReviewState` is the live-state projection over the audit-event stream (ADR-0011): every field that
// used to be denormalized into the document bytes — `author`, `rule`, `rationale`, `ruleVersion`, the
// suggestion→annotation links, the gate decision — is folded here from the events that already carry
// it, keyed by anchor id. The carrier keeps only the id; this map is where the detail lives.
//
// It is the *live* state, the exact mirror of what the bytes hold: an annotation whose anchor was
// removed from the document (rejected, stripped, withdrawn) drops out of the map, and a suggestion
// whose op-units were applied or rejected drops out too. That makes the map keyset equal to the live
// anchor set, so the U8 anchor↔state consistency check is a plain keyset comparison and projection
// dedup (R4) is a lookup over only the findings still in play. The richer historical view
// (rejected/kept counts over all time) stays in the separate `decisions`/`metrics` log-folds, which
// keep the rows this live fold discards.

export const AnnotationStatus = Schema.Literal("pending", "agreed", "reclassified", "disposed");
export type AnnotationStatus = typeof AnnotationStatus.Type;

// Each live entry is also a wire `Schema` (R6): the snapshot ships the folded `ReviewState` to the
// browser, so the projected detail crosses the WS hop as boundary-checked data, not an ad-hoc shape.
export const AnnotationReviewState = Schema.Struct({
  annotationId: AnnotationId,
  author: Author,
  rule: Schema.String,
  rationale: Schema.String,
  ruleVersion: Schema.optional(RuleContentHash),
  span: Schema.String,
  // The Expert-chosen problem scope, carried from the `annotation.created` event (undefined for
  // judge findings and legacy manual findings). Ships to the browser so the card can badge it.
  scope: Schema.optional(AnnotationScope),
  status: AnnotationStatus,
  // The id of the live suggestion resolving this annotation, if any — the one-hop cross-reference
  // `read-slice` follows now that the link has left the bytes (R7).
  resolvedBy: Schema.optional(SuggestionId),
});
export type AnnotationReviewState = typeof AnnotationReviewState.Type;

export const SuggestionReviewState = Schema.Struct({
  suggestionId: SuggestionId,
  resolves: Schema.Array(AnnotationId),
  reason: Schema.String,
  operations: Schema.Array(EditOperation),
});
export type SuggestionReviewState = typeof SuggestionReviewState.Type;

// A live Expert edit proposal awaiting confirm/discard. `sweeps` are the pending annotations its
// edits enclose — cleared (alongside the proposal) when it is applied, kept live when it is
// discarded. Mirrors `SuggestionReviewState` but carries no annotation it "resolves": an edit
// originates from no finding.
export const EditProposalReviewState = Schema.Struct({
  editProposalId: EditProposalId,
  reason: Schema.String,
  operations: Schema.Array(EditOperation),
  sweeps: Schema.Array(AnnotationId),
});
export type EditProposalReviewState = typeof EditProposalReviewState.Type;

export interface ReviewState {
  readonly byAnnotation: ReadonlyMap<AnnotationId, AnnotationReviewState>;
  readonly bySuggestion: ReadonlyMap<SuggestionId, SuggestionReviewState>;
  readonly byEditProposal: ReadonlyMap<EditProposalId, EditProposalReviewState>;
}

// The wire form of `ReviewState` for the snapshot (R6): the maps flatten to value arrays (the key is
// the entry's own id) in first-created order, so the client rebuilds the maps with the same insertion
// order downstream projections rely on.
export const ReviewStateWire = Schema.Struct({
  annotations: Schema.Array(AnnotationReviewState),
  suggestions: Schema.Array(SuggestionReviewState),
  editProposals: Schema.Array(EditProposalReviewState),
});
export type ReviewStateWire = typeof ReviewStateWire.Type;

export const reviewStateToWire = (state: ReviewState): ReviewStateWire => ({
  annotations: [...state.byAnnotation.values()],
  suggestions: [...state.bySuggestion.values()],
  editProposals: [...state.byEditProposal.values()],
});

export const reviewStateFromWire = (wire: ReviewStateWire): ReviewState => {
  const byAnnotation = new Map<AnnotationId, AnnotationReviewState>();
  for (const annotation of wire.annotations) byAnnotation.set(annotation.annotationId, annotation);
  const bySuggestion = new Map<SuggestionId, SuggestionReviewState>();
  for (const suggestion of wire.suggestions) bySuggestion.set(suggestion.suggestionId, suggestion);
  const byEditProposal = new Map<EditProposalId, EditProposalReviewState>();
  for (const proposal of wire.editProposals) byEditProposal.set(proposal.editProposalId, proposal);
  return { byAnnotation, bySuggestion, byEditProposal };
};

// A log event with the `seq` the append seam assigned it. `seq` is not a field on `AuditEvent` (it is
// the storage append order — the Postgres `BIGSERIAL` / the JSONL line position), so it rides
// alongside the event via the `eventsSince` read (U6). `foldReviewAt` consumes this shape to
// reconstruct review state at a historical point (R8, backtest parity).
export interface SeqEvent {
  readonly seq: number;
  readonly event: AuditEvent;
}

// The fold's working copy: the projected entry with its `readonly` lifted so events can mutate it in
// place (status flips, reclassify rewrites, cross-ref stamping). Structurally identical to
// `AnnotationReviewState`, so a built entry is returned as one without a cast.
type MutableAnnotation = { -readonly [K in keyof AnnotationReviewState]: AnnotationReviewState[K] };

// Apply one event to the in-progress maps — the byte transition its matching op makes, projected:
// `annotation.created` seeds the entry from the detail the event carries; later events mutate it
// (keep, reclassify, dispose) or remove it (reject/strip/withdraw). Map insertion order is
// first-created order, which downstream projections rely on for stable output.
//
// The `.v2` create arm seeds MANUAL findings only (ADR-0128): an automated `annotation.created.v2`
// still in an append-only log is a finding nothing can retract (see `projectOpenFindings`'s header).
// This fold feeds `pendingIds`/`isPending`, so admitting one would leave it forever open at Gate 1.
// The LEGACY markup-era `annotation.created` arm is deliberately not filtered: the markup era was
// retired wholesale long before — nothing appends one now, so those entries are frozen history.
const applyEvent = (
  byAnnotation: Map<AnnotationId, MutableAnnotation>,
  bySuggestion: Map<SuggestionId, SuggestionReviewState>,
  byEditProposal: Map<EditProposalId, EditProposalReviewState>,
  event: AuditEvent,
): void => {
  Match.value(event).pipe(
    Match.tag("annotation.created", (e) => {
      byAnnotation.set(e.annotationId, {
        annotationId: e.annotationId,
        author: e.author,
        rule: e.rule,
        rationale: e.rationale,
        ...(e.ruleVersion !== undefined ? { ruleVersion: e.ruleVersion } : {}),
        span: e.span,
        ...(e.scope !== undefined ? { scope: e.scope } : {}),
        status: "pending",
      });
    }),
    // R1: a v2 finding (structured anchor, no text span) seeds the entry the same way the legacy
    // arm does, so it enters the work queue instead of only rendering inline via `projectDiagnostics`.
    // `span` is left empty — v2 anchors are byte offsets resolved at fold time, not verbatim text —
    // and `ruleVersion` is left absent unless the event carries one, which no writer left on this
    // event since the staleness projection that read it was retired with the durable judge write (U7).
    Match.tag("annotation.created.v2", (e) => {
      // ADR-0128: only an Expert's finding is open work — see `projectOpenFindings`'s header.
      if (!isManualAnnotation(e)) return;
      byAnnotation.set(e.annotationId, {
        annotationId: e.annotationId,
        author: e.author,
        rule: e.rule,
        rationale: e.rationale,
        ...(e.ruleVersion !== undefined ? { ruleVersion: e.ruleVersion } : {}),
        span: "",
        ...(e.scope !== undefined ? { scope: e.scope } : {}),
        status: "pending",
      });
    }),
    // Every other terminal-for-annotation decision — keep, reclassify, reject/close/strip/retire, the
    // ADR-0049 accept-on-create terminals, and triage disposition — is classified by the one shared
    // classifier (CON-738) rather than a private tag list. Its outcome is a classification, not a
    // verdict: `kept` and `reclassified` are terminal but keep the live entry (status flip only —
    // the behaviour an OLD log with retired agreed/disagreed/closed construction still replays);
    // `ignored`/`removed` remove it, matching the accept-on-create terminals (ADR-0049: ignored (D1),
    // resolved (D3), retracted (D4)) and the legacy reject/close/strip/retire terminals identically. A
    // triage withdrawal removes the carrier the same way; a comment-only/suggest disposition keeps it
    // in place, terminal but still anchored — `disposed` carries the raw `disposition` so this is the
    // one arm that still branches on it.
    Match.tag("suggestion.created", (e) => {
      bySuggestion.set(e.suggestionId, {
        suggestionId: e.suggestionId,
        resolves: e.resolves,
        reason: e.reason,
        operations: e.operations,
      });
    }),
    // An amendment (Q8) replaces a live suggestion's operations/reason under the same id — the same
    // projection update `suggestion.created` performs, since the event carries the full replacement
    // shape (including `resolves`, preserved by the op). The suggestion never left `bySuggestion`
    // (amend refuses on a non-PENDING suggestion), so this is always an overwrite, never a fresh insert.
    Match.tag("suggestion.amended", (e) => {
      bySuggestion.set(e.suggestionId, {
        suggestionId: e.suggestionId,
        resolves: e.resolves,
        reason: e.reason,
        operations: e.operations,
      });
    }),
    // Accept applies the suggestion's op-units and clears the resolved annotations' carriers from the
    // bytes (`clearAnnotations`), so their live entries must leave with them — otherwise they linger
    // in `byAnnotation` with no anchor, failing `checkAnchorState` (`state-without-anchor`) on full
    // reconstruction and surfacing as phantom `missing` entries in `read-pending`. The `resolves` link
    // lives on the projected suggestion (the accept event carries only the id), so read it before the
    // delete.
    Match.tag("suggestion.accepted", (e) => {
      const suggestion = bySuggestion.get(e.suggestionId);
      if (suggestion !== undefined)
        for (const annotationId of suggestion.resolves) byAnnotation.delete(annotationId);
      bySuggestion.delete(e.suggestionId);
    }),
    // Reject removes only the suggestion carrier; the annotations it would have resolved stay live and
    // anchored (the reviewer rejected the fix, not the findings), so only the suggestion entry leaves.
    Match.tag("suggestion.rejected", (e) => {
      bySuggestion.delete(e.suggestionId);
    }),
    // An Expert edit proposal enters the work queue pending confirm/discard, carrying the findings it
    // sweeps so confirm can clear their live entries the way an accepted suggestion clears its
    // `resolves`.
    Match.tag("edit.proposed", (e) => {
      byEditProposal.set(e.editProposalId, {
        editProposalId: e.editProposalId,
        reason: e.reason,
        operations: e.operations,
        sweeps: e.sweeps,
      });
    }),
    // Confirm collapses the edit's op-units to clean text and clears any swept findings' carriers, so
    // their live entries must leave with the bytes (mirrors `suggestion.accepted`; the keep itself is
    // a separate `annotation.agreed` event the resolve op appends per sweep). Read `sweeps` off the
    // projected proposal before deleting it.
    Match.tag("edit.applied", (e) => {
      const proposal = byEditProposal.get(e.editProposalId);
      if (proposal !== undefined)
        for (const annotationId of proposal.sweeps) byAnnotation.delete(annotationId);
      byEditProposal.delete(e.editProposalId);
    }),
    // Discard strips the edit markup, restoring the prior bytes; swept findings were never decided, so
    // they stay live and pending — only the proposal entry leaves.
    Match.tag("edit.discarded", (e) => {
      byEditProposal.delete(e.editProposalId);
    }),
    Match.orElse((e) => {
      Match.value(classifyAnnotationTerminal(e)).pipe(
        Match.tag("kept", (o) => {
          const state = byAnnotation.get(o.annotationId);
          if (state !== undefined) state.status = "agreed";
        }),
        Match.tag("reclassified", (o) => {
          const state = byAnnotation.get(o.annotationId);
          if (state !== undefined) {
            state.rule = o.rule;
            state.rationale = o.rationale;
            state.status = "reclassified";
          }
        }),
        Match.tag("disposed", (o) => {
          if (Schema.is(Withdraw)(o.disposition)) {
            byAnnotation.delete(o.annotationId);
            return;
          }
          const state = byAnnotation.get(o.annotationId);
          if (state !== undefined) state.status = "disposed";
        }),
        Match.tag("ignored", "removed", (o) => {
          byAnnotation.delete(o.annotationId);
        }),
        Match.tag("not-terminal", () => {}),
        Match.exhaustive,
      );
    }),
  );
};

// One-hop cross-reference: stamp each live annotation with the live suggestion that resolves it, so a
// finding is followable in both directions even though the link no longer lives in the bytes (R7).
// Cleared first, because an incremental fold (`foldReviewEvent`) carries the prior stamp forward and a
// since-removed suggestion must not leave a dangling `resolvedBy`.
const stampResolvedBy = (
  byAnnotation: Map<AnnotationId, MutableAnnotation>,
  bySuggestion: Map<SuggestionId, SuggestionReviewState>,
): void => {
  for (const annotation of byAnnotation.values()) delete annotation.resolvedBy;
  for (const suggestion of bySuggestion.values())
    for (const annotationId of suggestion.resolves) {
      const annotation = byAnnotation.get(annotationId);
      if (annotation !== undefined) annotation.resolvedBy = suggestion.suggestionId;
    }
};

// Fold the whole event stream into live review state.
export const foldReview = (events: readonly AuditEvent[]): ReviewState => {
  const byAnnotation = new Map<AnnotationId, MutableAnnotation>();
  const bySuggestion = new Map<SuggestionId, SuggestionReviewState>();
  const byEditProposal = new Map<EditProposalId, EditProposalReviewState>();
  for (const event of events) applyEvent(byAnnotation, bySuggestion, byEditProposal, event);
  stampResolvedBy(byAnnotation, bySuggestion);
  return { byAnnotation, bySuggestion, byEditProposal };
};

// Advance a folded `ReviewState` by one event without re-folding the whole stream — the client's
// per-`Event` step over the WS hop (R6). Copies the maps so the prior state is left untouched, applies
// the single transition, and re-stamps the cross-references.
export const foldReviewEvent = (state: ReviewState, event: AuditEvent): ReviewState => {
  const byAnnotation = new Map<AnnotationId, MutableAnnotation>();
  for (const [id, annotation] of state.byAnnotation) byAnnotation.set(id, { ...annotation });
  const bySuggestion = new Map(state.bySuggestion);
  const byEditProposal = new Map(state.byEditProposal);
  applyEvent(byAnnotation, bySuggestion, byEditProposal, event);
  stampResolvedBy(byAnnotation, bySuggestion);
  return { byAnnotation, bySuggestion, byEditProposal };
};

// Review state as it stood at a historical `seq` (R8): fold only the events at or before the cursor.
// The text read model reconstructs the document at a `docVersion`; this reconstructs the review state
// that joined it, so the backtest and any read-at-version see a consistent pair.
export const foldReviewAt = (entries: readonly SeqEvent[], seq: number): ReviewState =>
  foldReview(entries.filter((entry) => entry.seq <= seq).map((entry) => entry.event));

// The structural dedup key (R4): rule canonicalised the way the write path canonicalises it (so a
// `[U6]`/`U6` notation mix collapses), joined to the verbatim span (which stays inline). Used to test
// whether a fresh `(rule, span)` already names a live annotation.
const dedupKey = (rule: string, span: string): string => `${canonicalizeRuleId(rule)} ${span}`;

// The live `(rule, span) → AnnotationId` index dedup queries instead of parsing the bytes (R4). A
// rejected/stripped annotation has already left `byAnnotation`, so it is absent here too — a re-run on
// a span whose prior finding was thrown away is *not* a no-op, exactly as before the rule left the
// bytes.
export const liveAnnotationIndex = (state: ReviewState): ReadonlyMap<string, AnnotationId> => {
  const index = new Map<string, AnnotationId>();
  for (const annotation of state.byAnnotation.values())
    index.set(dedupKey(annotation.rule, annotation.span), annotation.annotationId);
  return index;
};

// The live annotation a `(rule, span)` resolves to, if any — the projection lookup `write-annotation`
// runs inside the per-document critical section to decide structural idempotency (R4).
export const liveAnnotationFor = (
  state: ReviewState,
  rule: string,
  span: string,
): AnnotationId | undefined => liveAnnotationIndex(state).get(dedupKey(rule, span));

// The carrier (an anchor id in the reconstructed bytes) and the projected detail (a live
// `ReviewState` entry) must agree: every id inline has exactly one live state, and every live state
// has its id inline (R9). When the bytes and the projection diverge — a dangling carrier with no
// detail to join, or a live finding whose anchor was lost — a reader would otherwise render a blank
// finding or silently drop one, so the join fails loud instead.
export class AnchorStateMismatch extends Data.TaggedError("AnchorStateMismatch")<{
  readonly documentId: DocumentId;
  readonly kind: "annotation" | "suggestion";
  readonly id: string;
  readonly reason: "anchor-without-state" | "state-without-anchor";
}> {}

// Join the anchor ids parsed from reconstructed text against the live `ReviewState` keyset, both
// directions, and fail `AnchorStateMismatch` on the first divergence. Callers that materialize the
// *whole* document (so the keysets are comparable) run this at reconstruction; a windowed reader
// (`read-slice`) can only assert the `anchor-without-state` direction over its window.
export const checkAnchorState = (
  documentId: DocumentId,
  textAnnotationIds: readonly AnnotationId[],
  textSuggestionIds: readonly SuggestionId[],
  state: ReviewState,
): Effect.Effect<void, AnchorStateMismatch> =>
  Effect.gen(function* () {
    const annotationsInText = new Set<AnnotationId>(textAnnotationIds);
    const suggestionsInText = new Set<SuggestionId>(textSuggestionIds);
    for (const id of annotationsInText)
      if (!state.byAnnotation.has(id))
        return yield* Effect.fail(
          new AnchorStateMismatch({
            documentId,
            kind: "annotation",
            id,
            reason: "anchor-without-state",
          }),
        );
    for (const id of suggestionsInText)
      if (!state.bySuggestion.has(id))
        return yield* Effect.fail(
          new AnchorStateMismatch({
            documentId,
            kind: "suggestion",
            id,
            reason: "anchor-without-state",
          }),
        );
    for (const id of state.byAnnotation.keys())
      if (!annotationsInText.has(id))
        return yield* Effect.fail(
          new AnchorStateMismatch({
            documentId,
            kind: "annotation",
            id,
            reason: "state-without-anchor",
          }),
        );
    for (const id of state.bySuggestion.keys())
      if (!suggestionsInText.has(id))
        return yield* Effect.fail(
          new AnchorStateMismatch({
            documentId,
            kind: "suggestion",
            id,
            reason: "state-without-anchor",
          }),
        );
  });

// A single target (annotation or suggestion) receives at most ONE terminal event in a correct log:
// once a finding is decided (agreed / reclassified / disagreed / stripped / disposed) or a suggestion
// is accepted / rejected, `isPending` reports it closed and `resolve`'s single-shot guard refuses any
// further decision (N5). Within one process the per-document `transact` lock serialises guard-then-
// append, so a second terminal event for one target is structurally impossible.
//
// It becomes possible ACROSS processes, and this is the tripwire for that. The `transact` lock is a
// process-local in-memory keyed semaphore; a zero-byte terminal decision (a Gate-1 keep, a comment-
// only triage) moves no bytes, so the commit CAS — which compares reconstructed document text —
// passes for two concurrent identical resolves on two instances, and the partial
// `UNIQUE (document_id, doc_version)` audit index excludes zero-byte events (their `doc_version` is
// NULL). Two instances can therefore each append a phantom terminal event for the same target,
// silently double-counting metrics and leaving a duplicate audit pair. This is the cross-process
// resolve race documented in
// `docs/plans/2026-06-23-001-fix-cross-process-resolve-race-plan.md`, deliberately NOT fixed while the
// service runs single-instance (one process's in-memory lock fully serialises it). The day the system
// is scaled horizontally without first landing that advisory-lock fix, this check turns the otherwise
// silent corruption into a loud, named failure the next time the document's log is folded for a
// decision — the unambiguous signal to implement the plan before going further.
export class DuplicateTerminalDecision extends Data.TaggedError("DuplicateTerminalDecision")<{
  readonly documentId: DocumentId;
  readonly id: string;
  readonly kind: "annotation" | "suggestion" | "edit-proposal";
}> {}

const terminalTarget = (
  event: AuditEvent,
):
  | { readonly id: string; readonly kind: "annotation" | "suggestion" | "edit-proposal" }
  | undefined => {
  // Broadest of the five: every classifier outcome but `not-terminal` is a terminal decision here,
  // including `reclassified` (which does not remove the finding at any of the other four sites, but
  // is a one-shot decision this guard must still catch — see `annotation-terminal.ts`'s header).
  const outcome = classifyAnnotationTerminal(event);
  if (outcome._tag !== "not-terminal") return { id: outcome.annotationId, kind: "annotation" };
  return Match.value(event).pipe(
    Match.tag("suggestion.accepted", "suggestion.rejected", (e) => ({
      id: e.suggestionId,
      kind: "suggestion" as const,
    })),
    Match.tag("edit.applied", "edit.discarded", (e) => ({
      id: e.editProposalId,
      kind: "edit-proposal" as const,
    })),
    Match.orElse(() => undefined),
  );
};

// Fail `DuplicateTerminalDecision` on the first target carrying a second terminal event. Pure and
// total over the event stream so any authoritative server-side fold can guard with it; `resolveOp`
// runs it (dying loud) on the read of the document's log before deciding.
export const checkSingleTerminalDecision = (
  documentId: DocumentId,
  events: readonly AuditEvent[],
): Effect.Effect<void, DuplicateTerminalDecision> =>
  Effect.gen(function* () {
    const decided = new Set<string>();
    for (const event of events) {
      const target = terminalTarget(event);
      if (target === undefined) continue;
      const key = `${target.kind}:${target.id}`;
      if (decided.has(key))
        return yield* Effect.fail(
          new DuplicateTerminalDecision({ documentId, id: target.id, kind: target.kind }),
        );
      decided.add(key);
    }
  });

// ─── Diagnostics projection (U6) ──────────────────────────────────────────────
//
// `projectDiagnostics` maps every open annotation's structured anchor forward through
// the ChangeSets landed since its `atVersion`, producing version-keyed `DiagnosticAnchor`
// entries for all non-terminal annotations. This is the pure projection that the server
// service (apps/server/src/diagnostics.ts) calls after every land and annotation event.
//
// Design decisions (ADR-0043):
// - Only annotations with a structured anchor (`annotation.created.v2`) participate.
//   Legacy `annotation.created` events carry a text `span`, not byte offsets, and are
//   skipped. The annotation-writing ops (U7) will always write `.v2`; pre-migration logs
//   have no anchor to map and silently emit no diagnostic (migration is U15).
// - Terminal annotations (agreed, disagreed, closed, stripped, retired, and the ADR-0049
//   accept-on-create terminals ignored/resolved/retracted) are excluded by folding a
//   termination-only set from the event stream before projecting.
// - An anchor whose mapped span collapses to empty (from === to after ChangeSet.mapPos)
//   is retained as a `collapsed: true` zero-length entry at the collapse point.
// - Only Expert-authored findings participate (ADR-0128, `isManualAnnotation`).
// - Severity (U6, CON-737): a manual finding uses its own `annotation.created.v2` event's stamped
//   `severity` when present; a pre-U6 manual finding with none is "info" for a `manual:*`
//   sentinel rule and "warning" otherwise.

// One live entry for the diagnostics fold: the structured anchor + annotation metadata.
interface LiveAnnotationEntry {
  readonly annotationId: AnnotationId;
  readonly rule: string;
  readonly rationale: string;
  readonly from: number;
  readonly to: number;
  readonly atVersion: CollabVersion;
  readonly author: Author;
  // The display NAME of the person behind a manual finding, derived here from the event's
  // `createdBy` so the raw `ViewerId` never reaches `DiagnosticAnchor` (ADR-0013). Undefined for
  // manual findings predating `createdBy`.
  readonly authorName: string | undefined;
  // The manual finding's stamped severity (CON-737), carried straight from the
  // `annotation.created.v2` event. Undefined for a manual finding predating this field.
  readonly severity: FindingSeverity | undefined;
}

// Derive the severity for a diagnostic entry (U6, CON-737).
// Precedence: the Expert's own stamped severity, if present, wins outright. Otherwise (a manual
// finding with no stamp — pre-U6) a `manual:*` sentinel (the retired `manual:unclassified`, or the
// current write-time `manual:finding`, per manual.ts) → "info"; any other rule → "warning".
const deriveSeverity = (
  rule: string,
  stampedSeverity: FindingSeverity | undefined,
): FindingSeverity => {
  if (stampedSeverity !== undefined) return stampedSeverity;
  return isManualSentinel(canonicalizeRuleId(rule)) ? "info" : "warning";
};

// Pure projection: open annotations → version-keyed diagnostics mapped to `headVersion`.
//
// Parameters:
//   events         — the full audit-event stream for the document (in order)
//   updateLog      — the derived collab update log (ChangeSetJson entries ordered by version)
//   headVersion    — the current document version the diagnostics should be keyed to
//
// Returns `{ diagnostics, atVersion }` where `atVersion === headVersion`.
// Pure and total: the same inputs always produce the same output; no IO, no hidden state.
//
// Membership is BOTH the terminal-event fold (step 1) and de-anchoring (step 2, CON-747): a manual
// finding whose anchored passage a later land rewrote is not open at head and is omitted. That makes
// this projection — not `projectOpenFindings` — the authority on what is open at head; `read-findings`
// joins against it for exactly that reason.
export const projectDiagnostics = (
  events: readonly AuditEvent[],
  updateLog: UpdateLog,
  headVersion: CollabVersion,
  // `lint` (CON-500), `audit` (CON-744), `findingSummary` (CON-737, U6), `auditRan` (ADR-0095,
  // CON-893) and `conversion` (CON-1314) are not this projection's concern — all are derived fresh
  // from head text/state and joined in by the caller (`computeDiagnostics`), never from the
  // audit-event fold this function performs — so all are excluded from this function's own
  // return type.
): Omit<
  DiagnosticsPayload,
  | "_tag"
  | "docId"
  | "lint"
  | "audit"
  | "findingSummary"
  | "auditRan"
  | "enrichmentReadiness"
  | "conversion"
> => {
  // Step 1: collect live DURABLY-WRITTEN annotations with structured anchors.
  // Fold the event stream to find all annotation.created.v2 events, then remove those
  // that have a terminal event (agreed.v2, disagreed.v2, closed.v2, stripped.v2,
  // and legacy terminal forms for annotations without .v2 anchor). Only an Expert's create is
  // folded (ADR-0128) — see `projectOpenFindings`'s header, whose live set this one MUST keep
  // matching.
  const liveById = new Map<AnnotationId, LiveAnnotationEntry>();

  for (const event of events) {
    Match.value(event).pipe(
      // New structured annotation
      Match.tag("annotation.created.v2", (e) => {
        if (!isManualAnnotation(e)) return;
        liveById.set(e.annotationId, {
          annotationId: e.annotationId,
          rule: e.rule,
          rationale: e.rationale,
          from: e.anchor.from,
          to: e.anchor.to,
          atVersion: e.anchor.atVersion,
          author: e.author,
          authorName:
            e.createdByDisplayName ??
            (e.createdBy === undefined ? undefined : deriveDisplayName(e.createdBy)),
          severity: e.severity,
        });
      }),
      // Every other terminal-for-annotation decision drops the diagnostic, via the one shared
      // classifier (CON-738) — `agreed`(.v2) stays terminal for this fold (a kept finding leaves the
      // rail), the ADR-0049 accept-on-create terminals (ignored/resolved/retracted) and the legacy
      // reject/close/strip/retire terminals drop it identically, and any triage disposition terminates
      // it. `reclassified` is excluded — matching `projectOpenFindings`, which this set MUST match
      // exactly (see that module's header).
      Match.orElse((e) => {
        const outcome = classifyAnnotationTerminal(e);
        if (outcome._tag !== "not-terminal" && outcome._tag !== "reclassified") {
          liveById.delete(outcome.annotationId);
        }
      }),
    );
  }

  // Step 2: map each live annotation's anchor forward through ChangeSets since atVersion.
  // Only include update-log entries with version > annotation.atVersion and <= headVersion.
  const diagnostics: DiagnosticAnchor[] = [];
  // Every live annotation maps through an overlapping suffix of the same update log, so each
  // ChangeSet is parsed once per projection rather than once per annotation.
  const parsed = new Map<UpdateLogEntry, ChangeSet>();
  const changeSetOf = (update: UpdateLogEntry): ChangeSet => {
    let cs = parsed.get(update);
    if (cs === undefined) {
      cs = ChangeSet.fromJSON(update.changes);
      parsed.set(update, cs);
    }
    return cs;
  };

  for (const entry of liveById.values()) {
    const applicableChanges = updateLog.filter(
      (u) => u.version > entry.atVersion && u.version <= headVersion,
    );

    let from = entry.from;
    let to = entry.to;
    let deAnchored = false;

    for (const update of applicableChanges) {
      const cs = changeSetOf(update);
      // De-anchoring test, run against the anchor's coordinates BEFORE this changeset is applied —
      // the space `iterChanges` reports `fromA`/`toA` in. A non-zero-length anchor keeps the
      // original rule regardless of the change's own length: a change region overlapping its
      // interior, exclusive of both edges (so an insertion exactly at either edge is kept, not
      // de-anchored — confirmed by this file's own tests; this deliberately differs from
      // `regionsOverlap`'s own zero-length-change convention, which is left-inclusive). A
      // zero-length anchor (a manual finding created with `from === to`) instead mirrors
      // `regionsOverlap`'s zero-length convention (ADR-0088 §1): it de-anchors on a change region
      // that starts at or before it and ends after it, including a pure insertion exactly at that
      // point — a case the exclusive-both-edges rule could never satisfy for a zero-length anchor.
      if (!deAnchored) {
        const anchorZero = from === to;
        cs.iterChanges((fromA, toA) => {
          if (deAnchored) return;
          if (!anchorZero) deAnchored = fromA < to && toA > from;
          else if (fromA === toA) deAnchored = fromA === from;
          else deAnchored = fromA <= from && from < toA;
        });
      }
      // Map `from` with assoc=1 (bias rightward): insertions AT `from` push the anchor
      // rightward so it continues to point at the annotated text, not the newly inserted
      // content before it.
      // Map `to` with assoc=-1 (bias leftward): insertions AT `to` go after the anchor
      // rather than expanding it — the anchor does not grow to include new content appended
      // at its right edge.
      const newFrom = cs.mapPos(from, 1);
      const newTo = cs.mapPos(to, -1);
      // Normalise THIS changeset's result before carrying it into the next one (CON-753; ADR-0045
      // amendment 2026-08-07). A replacement that ENGULFS the span inverts it: `MapMode.Simple`
      // sends a position strictly inside a replaced region `[posA, endA)` → `[posB, posB + ins)` to
      // `posB + ins` for assoc=+1 and to `posB` for assoc=-1, so `newFrom > newTo`. Left unnormalised
      // the two ends then map INDEPENDENTLY through every later changeset and the offsets compound
      // into positions unrelated to the annotated text. `Math.min` is `posB` — the start of the
      // replaced region, and for a plain deletion the deletion point itself (which never inverts, so
      // this is a no-op there). `collapsed` is still derived below from `from >= to`, unchanged: a
      // span that collapses today still collapses, it just collapses at the left edge.
      const collapsePoint = Math.min(newFrom, newTo);
      from = newFrom > newTo ? collapsePoint : newFrom;
      to = newFrom > newTo ? collapsePoint : newTo;
    }

    // De-anchored (CON-747): a manual finding is an Expert's judgement about a specific passage.
    // Once a land has rewritten that passage the finding is no longer about anything at head, so it
    // leaves the rail — and, through this same projection, `read-findings`. Nothing is appended to
    // the log: the `annotation.created.v2` event and its rationale are retained for later mining,
    // and a revert restoring the text brings the finding back.
    if (deAnchored) continue;

    // Collapse detection: after mapping, if from >= to the anchor was deleted.
    const collapsed = from >= to;

    diagnostics.push({
      annotationId: entry.annotationId,
      rule: entry.rule,
      rationale: entry.rationale,
      from,
      to: collapsed ? from : to,
      ...(collapsed ? { collapsed: true } : {}),
      severity: deriveSeverity(entry.rule, entry.severity),
      message: entry.rationale,
      author: entry.author,
      ...(entry.authorName === undefined ? {} : { authorName: entry.authorName }),
    });
  }

  return { diagnostics, atVersion: headVersion };
};
